import React, {Fragment} from 'react'
import useEffect from 'admin/hooks/useEffect'
import useDebit from 'bundles/admin/hooks/useDebit'
import useDocument from 'admin/hooks/useDocument'
import useBoolState from 'admin/hooks/useBoolState'
import useStateObject from 'admin/hooks/useStateObject'
import InputField from 'admin/components/shared/InputField'
import DateField from 'admin/components/shared/DateField'
import Modal from 'admin/components/shared/Modal'
import Header from './shared/Header'
import ShowField from './shared/ShowField'
import Deletion from './Deletion'
import {useParams, useHistory} from 'react-router-dom'
import useModal from '../hooks/useModal'
import ModalLink from './shared/ModalLink'

export default () => {
  const history = useHistory()
  const {organisationID} = useParams()
  const {debitId, accountId, popModal} = useModal()
  const {debit, errors, update, destroy} = useDebit(organisationID, accountId, debitId)
  const [showConfirmDelete, setConfirmDelete, toggleConfirmDelete] = useBoolState()
  const [form, setForm, resetForm] = useStateObject(debit)

  const title = `Debit ${debitId}`
  useDocument({title})

  useEffect(() => resetForm(debit), [debit], false)

  const handleUpdate = e => {
    e.preventDefault()
    update(form)
  }

  const handleChange = changes => setForm(changes)

  const handleClose = e => {
    e.preventDefault()
    resetForm(debit)
    popModal('debit', ['debitId', 'accountId'])
  }

  if (!debit.id) return null

  const locked = debit.state === 'locked'

  return (
    <Fragment>
      <Modal onClose={handleClose} flashId='edit-account-debit'>
        {locked && (
          <section className='py-3 section has-background-dark'>
            <div className='container'>
              <span className='icon-text'>
                <span className='icon'>
                  <i className='fas fa-lock'></i>
                </span>
                <span className='is-size-7 has-text-light ml-2'>This debit is locked and cannot be edited.</span>
              </span>
            </div>
          </section>
        )}
        <Header
          title={title}
          subtitle={
            !!debit.xeroLink ? (
              <h5 className='subtitle is-7'>
                <a href={debit.xeroLink} target='_blank'>
                  <span>Imported from Xero</span>{' '}
                  <span className='icon'>
                    <i className='fa fa-external-link'></i>
                  </span>
                </a>
              </h5>
            ) : null
          }>
          <ModalLink modal='history' params={{itemtype: 'Debit', itemId: debitId}} className='button is-white'>
            <span className='icon is-small'>
              <i className='fa fa-history' aria-hidden='true'></i>
            </span>
            <span>View History</span>
          </ModalLink>
          {locked ? null : (
            <Fragment>
              <button className='button is-danger is-outlined' onClick={toggleConfirmDelete}>
                Delete
              </button>
              <button className='button is-primary' onClick={handleUpdate}>
                Save
              </button>
            </Fragment>
          )}
        </Header>
        <section className='section is-narrow'>
          <div className='container'>
            <form>
              <div className='field is-grouped'>
                <div className='control is-expanded'>
                  <DateField
                    name='date'
                    value={form.date || ''}
                    onChange={handleChange}
                    errors={errors.date}
                    disabled={locked || !!debit.xeroLink}
                    required
                  />
                </div>

                <div className='control is-expanded'>
                  <InputField
                    type='number'
                    name='amount'
                    value={form.amount || ''}
                    min={0}
                    onChange={handleChange}
                    errors={errors.amount}
                    autoFocus={true}
                    disabled={locked || !!debit.xeroLink}
                    required
                  />
                </div>
              </div>

              <div className='field'>
                <div className='control'>
                  <InputField
                    type='text'
                    name='description'
                    value={form.description || ''}
                    onChange={handleChange}
                    errors={errors.description}
                    disabled={locked}
                    required
                  />
                </div>
              </div>
            </form>
          </div>
        </section>
        {!!debit.invoice ? (
          <section className='py-3 section has-background-info'>
            <div className='container is-size-7 has-text-light'>
              <ShowField
                value={debit.invoice?.invoiceNumber}
                href={`/orgs/${organisationID}/invoices/${debit.invoice?.id}`}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Attached to'
              />
            </div>
          </section>
        ) : null}
      </Modal>

      {showConfirmDelete && (
        <Deletion
          confirm='Are you sure you want to delete?'
          onConfirm={() => destroy({}, history.goBack)}
          onClose={toggleConfirmDelete}
        />
      )}
    </Fragment>
  )
}
